
import { supabase } from '@/lib/supabase';
import { adminError} from '@/lib/utils/admin-logger';

// Get review requests for a reviewer - renamed from getReviewRequests
export const getReviewRequests = async (userId: string): Promise<any[]> => {
  try {
    // Use the security-definer RPC that returns the safe column subset for
    // the assigned reviewer. The base review_requests table is requestor-only.
    const { data: rows, error } = await supabase
      .rpc('get_review_requests_for_reviewer');

    if (error) throw error;

    // Sort newest first (matches previous .order('created_at', { ascending: false }))
    const sorted = [...(rows ?? [])].sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    // Hydrate paper details separately (no FK in schema; same pattern as before).
    const paperIds = Array.from(new Set(sorted.map((r: any) => r.paper_id))).filter(Boolean);
    const papersById = new Map<string, any>();
    if (paperIds.length > 0) {
      const { data: papers } = await supabase
        .from('papers')
        .select('id, title, abstract, author_id, category, current_version')
        .in('id', paperIds as string[]);
      (papers || []).forEach((p) => papersById.set(p.id, p));
    }

    const data = sorted.map((r: any) => ({ ...r, papers: papersById.get(r.paper_id) ?? null }));

    // If we need reviewer details, we need to fetch them separately
    // since there's no direct foreign key relationship in the schema
    const enhancedData = await enhanceWithUserDetails(data);
    
    // Transform data to match our ReviewRequest type
    return enhancedData.map(request => ({
      id: request.id,
      paperId: request.paper_id,
      versionId: request.version_id || '',
      requesterId: request.requestor_id,
      reviewerId: request.reviewer_id,
      requestedReviewers: [request.reviewer_id].filter(Boolean),
      status: request.status,
      createdAt: request.created_at,
      reviews: [],
      dueDate: request.due_date,
      paperInfo: request.papers ? {
        id: request.papers.id,
        title: request.papers.title,
        abstract: request.papers.abstract,
        category: request.papers.category
      } : undefined,
      reviewerName: request.reviewer?.name,
      requestorName: request.requestor?.name
    }));
  } catch (error) {
    adminError('Error fetching review requests:', error);
    return [];
  }
};

// Get review request by ID
export const getReviewRequestById = async (requestId: string): Promise<any | null> => {
  try {
    const { data, error } = await supabase
      .from('review_requests')
      .select(`
        *,
        papers:paper_id (
          id, title, abstract, author_id, category, current_version
        )
      `)
      .eq('id', requestId)
      .maybeSingle();
    
    if (error) throw error;
    if (!data) return null;
    
    // Enhance with user details
    const enhancedData = await enhanceWithUserDetails([data]);
    const enhancedRequest = enhancedData[0];
    
    if (!enhancedRequest) return null;
    
    // Transform to match our type
    return {
      id: enhancedRequest.id,
      paperId: enhancedRequest.paper_id,
      versionId: enhancedRequest.version_id || '',
      requesterId: enhancedRequest.requestor_id,
      reviewerId: enhancedRequest.reviewer_id,
      requestedReviewers: [enhancedRequest.reviewer_id].filter(Boolean),
      status: enhancedRequest.status,
      createdAt: enhancedRequest.created_at,
      reviews: [],
      dueDate: enhancedRequest.due_date,
      paperInfo: enhancedRequest.papers ? {
        id: enhancedRequest.papers.id,
        title: enhancedRequest.papers.title,
        abstract: enhancedRequest.papers.abstract,
        category: enhancedRequest.papers.category
      } : undefined,
      reviewerName: enhancedRequest.reviewer?.name
    };
  } catch (error) {
    adminError('Error fetching review request:', error);
    return null;
  }
};

// Get review requests for a specific paper
export const getPaperReviewRequests = async (paperId: string): Promise<any[]> => {
  try {
    // First, get the basic review request data with the paper details
    const { data, error } = await supabase
      .from('review_requests')
      .select(`
        *,
        papers:paper_id (
          id, title, abstract, author_id, category, current_version
        )
      `)
      .eq('paper_id', paperId)
      .order('created_at', { ascending: false });
    
    if (error) {
      adminError('Error in getPaperReviewRequests:', error);
      throw error;
    }
    
    // Now enhance the data with user details
    const enhancedData = await enhanceWithUserDetails(data || []);
    
    // Transform to match our type
    return enhancedData.map(request => ({
      id: request.id,
      paperId: request.paper_id,
      versionId: request.version_id || '',
      requesterId: request.requestor_id,
      reviewerId: request.reviewer_id,
      requestedReviewers: [request.reviewer_id].filter(Boolean),
      status: request.status,
      createdAt: request.created_at,
      reviews: [],
      dueDate: request.due_date,
      paperInfo: request.papers ? {
        id: request.papers.id,
        title: request.papers.title,
        abstract: request.papers.abstract,
        category: request.papers.category
      } : undefined,
      reviewerName: request.reviewer?.name
    }));
  } catch (error) {
    adminError('Error fetching paper review requests:', error);
    return [];
  }
};

// Helper function to enhance review requests with user details
const enhanceWithUserDetails = async (requests: any[]): Promise<any[]> => {
  if (requests.length === 0) return [];
  
  try {
    // Get unique user IDs (both reviewer and requestor)
    const userIds = new Set<string>();
    requests.forEach(req => {
      if (req.reviewer_id) userIds.add(req.reviewer_id);
      if (req.requestor_id) userIds.add(req.requestor_id);
    });
    
    if (userIds.size === 0) return requests;
    
    // Fetch user details in one batch
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id, name, role, institution')
      .in('id', Array.from(userIds));
      
    if (userError) {
      adminError('Error fetching user details:', userError);
      throw userError;
    }
    
    // Create a map for quick lookups
    const userMap = new Map();
    userData?.forEach(user => userMap.set(user.id, user));
    
    // Enhance each request with user details
    return requests.map(req => ({
      ...req,
      reviewer: req.reviewer_id ? userMap.get(req.reviewer_id) : null,
      requestor: req.requestor_id ? userMap.get(req.requestor_id) : null
    }));
    
  } catch (error) {
    adminError('Error enhancing review requests with user details:', error);
    return requests; // Return original data if enhancement fails
  }
};

// Update a review request status
export const updateReviewRequestStatus = async (requestId: string, status: string, reviewerId?: string): Promise<boolean> => {
  try {
    const updateData: any = { status };
    
    // If reviewerId is provided, update that as well
    if (reviewerId) {
      updateData.reviewer_id = reviewerId;
    }
    
    const { error } = await supabase
      .from('review_requests')
      .update(updateData)
      .eq('id', requestId);
    
    if (error) throw error;
    return true;
  } catch (error) {
    adminError('Error updating review request status:', error);
    return false;
  }
};

// Create a review request
export const createReviewRequest = async (
  paperId: string, 
  requestorId: string, 
  reviewerId: string, 
  versionId?: string,
  dueDate?: string
): Promise<any> => {
  try {
    const { data, error } = await supabase
      .from('review_requests')
      .insert({
        paper_id: paperId,
        requestor_id: requestorId,
        reviewer_id: reviewerId,
        version_id: versionId,
        due_date: dueDate
      })
      .select('*')
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    adminError('Error creating review request:', error);
    return null;
  }
};
